import React, { useState } from "react";
import { useLocalStore, useObserver } from "mobx-react";
import { Form } from "@modules/formx-react";
import { useNavigation } from "@navigation";
import locale from "@bonliva-crm/locale";
import { ErrorCard, Spinner, TabBadge } from "@components";
import { FormxDebugger } from "@components/formx";
import { TabFormLayout } from "@components/forms/TabFormLayout";
import { ErrorBoundary } from "@components/error-boundary";
import { BisnodeLookup } from "@containers";
import { ConsultantEditorStore } from "./ConsultantEditorStore";
import { ConsultantEditorContext } from "./ConsultantEditorContext";
import { PersonalTab } from "./tabs/PersonalTab";
import { BackgroundTab } from "./tabs/BackgroundTab";
import { EconomicsTab } from "./tabs/EconomicsTab";
import { ExpertiseTab } from "./tabs/ExpertiseTab";
import { MiscTab } from "./tabs/MiscTab";
import { FormMessages } from "./sections/FormMessages";
import { ImportCVInfo } from "./sections/ImportCVInfo";
import { ToggleMarkForDelete } from "./ToggleMarkForDelete";
import { LeadStatusModal } from "@consultants/consultant-profile-new/lead-status/lead-status-modal";
import { ThemeProvider } from "@material-ui/core";
import { bonlivaPipelineTheme } from "@bonliva-crm/ui";
import {
  ConsultantBookingStatus,
  useConsultantProfile_GetConsultantQuery
} from "@bonliva-crm/graphql";

type Props = {
  consultantId: string;
};

export function ConsultantEditor({ consultantId }: Props) {
  const navigation = useNavigation();
  const [leadStatusModalOpen, setLeadStatusModalOpen] = useState(false);
  const consultantQuery = useConsultantProfile_GetConsultantQuery({
    fetchPolicy: "cache-first",
    variables: {
      id: consultantId
    },
    skip: !consultantId
  });

  const localStore = useLocalStore(
    () =>
      new ConsultantEditorStore(
        consultantId,
        navigation
      )
  );

  return useObserver(() => {
    if (localStore.isLoading) {
      return <Spinner />;
    }

    if (localStore.error) {
      return (
        <ErrorCard
          centered={true}
          message={localStore.error}
          title="Error occurred while loading consultant"
        />
      );
    }

    const allowCV = true;

    const onSubmit = () => {
      if (
        consultantQuery.data?.consultantById.bookingStatus ===
          ConsultantBookingStatus.NeverBooked &&
        consultantQuery.data?.consultantById.ResponsiblePerson &&
        !localStore.form.values.responsiblePerson
      ) {
        setLeadStatusModalOpen(true);
      } else {
        localStore.form.submit();
      }
    };

    return (
      <ErrorBoundary>
        <ConsultantEditorContext.Provider value={localStore}>
          <Form store={localStore.form} allowUpdates>
            <TabFormLayout
              beforeContent={<FormMessages data={localStore.contactsValidate} />}
              blockId="consultant_form"
              canSubmit={
                localStore.form.canSubmit &&
                localStore.consultantCVManager.validated &&
                !localStore.isSsnDuplicated
              }
              isDirty={localStore.form.dirty}
              isSubmitting={localStore.form.isSubmitting}
              onSubmit={onSubmit}
              onReset={() => localStore.form.reset()}
              onClose={navigation.close}
              extraButtons={
                <>
                  {localStore.canDelete && (
                    <ToggleMarkForDelete
                      id={consultantId}
                      status={localStore.form.values.status}
                      disabled={localStore.form.isSubmitting || localStore.isNew}
                    />
                  )}

                  {allowCV && <ImportCVInfo />}
                </>
              }
              tabs={[
                {
                  label: (
                    <TabBadge
                      label={locale.PersonalTab}
                      hasErrors={localStore.hasErrorsInPersonalTab}
                      hasChangedValues={
                        localStore.consultantCVManager.hasChangesInPersonalTab
                      }
                    />
                  ),
                  value: "personal",
                  content: () => <PersonalTab />
                },
                {
                  label: (
                    <TabBadge
                      label={locale.BackgroundTab}
                      hasErrors={localStore.hasErrorsInBackgroundTab}
                      hasChangedValues={
                        localStore.consultantCVManager.hasChangesInBackgroundTab
                      }
                    />
                  ),
                  value: "background",
                  content: () => <BackgroundTab />
                },
                {
                  label: (
                    <TabBadge
                      label={locale.Expertise}
                      hasErrors={localStore.hasErrorsInExpertiseTab}
                      hasChangedValues={
                        localStore.consultantCVManager.hasChangesInExpertiseTab
                      }
                    />
                  ),
                  value: "expertise",
                  content: () => <ExpertiseTab />
                },
                {
                  label: (
                    <TabBadge
                      label={locale.Economy}
                      hasErrors={localStore.hasErrorsInEconomyTab}
                      hasChangedValues={
                        localStore.consultantCVManager.hasChangesInEconomyTab
                      }
                    />
                  ),
                  value: "economics",
                  content: () => <EconomicsTab />
                },
                {
                  label: (
                    <TabBadge
                      label={locale.Misc}
                      hasErrors={localStore.hasErrorsInMiscTab}
                      hasChangedValues={
                        localStore.consultantCVManager.hasChangesInMiscTab
                      }
                    />
                  ),
                  value: "misc",
                  content: () => <MiscTab />
                }
              ]}
            />

            <FormxDebugger />
          </Form>

          <BisnodeLookup store={localStore.bisnodeLookup} />
        </ConsultantEditorContext.Provider>
        {!localStore.isNew && (
          <ThemeProvider theme={bonlivaPipelineTheme}>
            <LeadStatusModal
              consultantId={consultantId}
              type={"deAssignHBA"}
              open={leadStatusModalOpen}
              setOpen={setLeadStatusModalOpen}
              onSubmit={localStore.form.submit}
            />
          </ThemeProvider>
        )}
      </ErrorBoundary>
    );
  });
}
